import type { ReactNode } from "react";
import type { AppLayout, FieldDef, LayoutPage, LayoutSection } from "../../lib/opsApi";
import { resolveFieldHint } from "../../help/fieldHints";
import { DetailSection, FieldItem } from "./RecordWorkspace";
import {
  findField,
  formatFieldValue,
  normalizeLayoutPages,
  recordFieldValue,
  sampleFieldValue,
  slotIsWide,
} from "./recordLayout";

export function detailsPage(layout: AppLayout): LayoutPage | undefined {
  const pages = normalizeLayoutPages(layout.layout.pages);
  return pages.find((page) => page.kind !== "related") ?? pages[0];
}

function sectionIsEmpty(section: LayoutSection, record: Record<string, unknown> | undefined): boolean {
  return section.fields.every((slot) => {
    const value = recordFieldValue(record, slot.field_key);
    return value == null || value === "";
  });
}

export function DesignedRecordFields({
  layout,
  record,
  hideEmptySections = false,
  pageKey,
  selectedFieldKey,
  selectedSectionKey,
  onSelectField,
  onSelectSection,
  onEditSection,
  onDeleteSection,
  sampleEntity,
  emptyHint,
  renderValue,
  respectVisibility = true,
}: {
  layout: AppLayout;
  record?: Record<string, unknown>;
  hideEmptySections?: boolean;
  pageKey?: string;
  selectedFieldKey?: string | null;
  selectedSectionKey?: string | null;
  onSelectField?: (fieldKey: string, sectionKey: string) => void;
  onSelectSection?: (sectionKey: string) => void;
  onEditSection?: (sectionKey: string) => void;
  onDeleteSection?: (sectionKey: string) => void;
  sampleEntity?: string;
  emptyHint?: ReactNode;
  renderValue?: (info: { fieldKey: string; formatted: string; field?: FieldDef }) => ReactNode;
  /** When true (default), fields toggled off in App Designer are hidden here. The App Designer's own layout-editing preview passes false so every placed field stays visible for editing. */
  respectVisibility?: boolean;
}) {
  const pages = normalizeLayoutPages(layout.layout.pages);
  const page = pageKey ? pages.find((item) => item.key === pageKey) : detailsPage(layout);
  const sections = page?.sections ?? [];
  if (sections.length === 0) {
    return emptyHint ?? null;
  }

  return (
    <>
      {sections.map((section) => {
        if (hideEmptySections && sectionIsEmpty(section, record)) return null;
        const interactive = Boolean(onSelectSection || onSelectField || onEditSection);
        return (
          <DetailSection
            key={section.key}
            title={section.label}
            className={selectedSectionKey === section.key ? "is-selected" : undefined}
            onTitleClick={onEditSection ? () => onEditSection(section.key) : undefined}
            tools={
              interactive ? (
                <div className="crm-designer-section-tools">
                  <button
                    type="button"
                    className={`crm-designer-place${selectedSectionKey === section.key ? " is-active" : ""}`}
                    onClick={() => onSelectSection?.(section.key)}
                  >
                    {selectedSectionKey === section.key ? "Adding fields here" : "Place fields here"}
                  </button>
                  {onDeleteSection && (page?.sections?.length ?? 0) > 1 ? (
                    <button
                      type="button"
                      className="crm-designer-delete-section"
                      onClick={() => onDeleteSection(section.key)}
                    >
                      Delete section
                    </button>
                  ) : null}
                </div>
              ) : null
            }
          >
            {section.fields.length === 0 ? (
              <div className="crm-field-item is-wide crm-designer-empty-slot">
                <div className="crm-field-item-label">Empty section</div>
                <div className="crm-field-item-value">Add a field from the catalogue into this block.</div>
              </div>
            ) : (
              section.fields
                .filter((slot) => {
                  if (!respectVisibility) return true;
                  const field = findField(layout.fields, slot.field_key);
                  return field ? field.visible : true;
                })
                .map((slot) => {
                  const field = findField(layout.fields, slot.field_key);
                  const formatted = sampleEntity
                    ? sampleFieldValue(sampleEntity, field)
                    : formatFieldValue(recordFieldValue(record, slot.field_key));
                  const value = renderValue
                    ? renderValue({ fieldKey: slot.field_key, formatted, field })
                    : formatted;
                  return (
                    <FieldItem
                      key={`${section.key}-${slot.field_key}`}
                      label={field?.label ?? slot.field_key}
                      hint={resolveFieldHint(slot.field_key, field?.ai_meaning)}
                      value={value}
                      wide={slotIsWide(slot)}
                      selected={selectedFieldKey === slot.field_key}
                      meta={onSelectField ? field?.field_type : undefined}
                      onSelect={onSelectField ? () => onSelectField(slot.field_key, section.key) : undefined}
                    />
                  );
                })
            )}
          </DetailSection>
        );
      })}
    </>
  );
}
